/**
 * The one scroll source for the whole app (DESIGN_SYSTEM §3): header state, specular sheen, parallax
 * and scroll-scrubbed stages all subscribe here instead of each attaching their own `scroll` handler.
 *
 *  • A single passive `scroll` (+ `resize`) listener, attached only while somebody is subscribed.
 *  • Listeners are invoked at most once per animation frame, with an immutable snapshot.
 *  • It reads `window.scrollY`, so it works identically with native scrolling and with Lenis (which
 *    scrolls the window itself); smooth scrolling is registered separately and only adds
 *    `scrollTo` / `lock` behaviour.
 *
 * `createScrollStore` is exported for tests; the app uses the `scrollStore` singleton.
 */

export interface ScrollState {
  /** Scroll offset in CSS px (never negative — iOS rubber-banding is clamped). */
  readonly y: number;
  /** Distance moved since the previous frame (px, signed). */
  readonly velocity: number;
  /** Last non-zero direction of travel: 1 = down, -1 = up, 0 = not scrolled yet. */
  readonly direction: -1 | 0 | 1;
  readonly viewportHeight: number;
  readonly viewportWidth: number;
}

export type ScrollListener = (state: ScrollState) => void;

/** The bits of Lenis the store needs (kept structural so the library stays a lazy chunk). */
export interface SmoothScroller {
  scrollTo: (target: number, options?: { immediate?: boolean; duration?: number }) => void;
  stop: () => void;
  start: () => void;
}

export interface ScrollStore {
  getState: () => ScrollState;
  /** Subscribe; the listener is called once immediately with the current state, then once per scrolling frame. */
  subscribe: (listener: ScrollListener) => () => void;
  /** Force a fresh measurement (after layout changes, route changes, font loads). */
  refresh: () => void;
  scrollTo: (y: number, options?: { immediate?: boolean }) => void;
  /**
   * End any glide the smooth scroller is still playing (Lenis keeps animating for about a second after the last
   * wheel event) so the caller can move the window itself without the scroller writing its own position over
   * it on the next frame. A no-op without a smooth scroller, and while a lock holds it stopped (it is not
   * animating then). The scroller picks the window's position up again from its native scroll events.
   */
  settle: () => void;
  /** Freeze page scrolling (dialogs, menus). Returns the release function; locks are counted. */
  lock: () => () => void;
  registerSmoothScroller: (scroller: SmoothScroller | null) => void;
  readonly smooth: boolean;
}

const INITIAL: ScrollState = {
  y: 0,
  velocity: 0,
  direction: 0,
  viewportHeight: 0,
  viewportWidth: 0,
};

export function createScrollStore(
  win: Window | undefined = typeof window === 'undefined' ? undefined : window,
): ScrollStore {
  const listeners = new Set<ScrollListener>();
  let state: ScrollState = INITIAL;
  let attached = false;
  let frame = 0;
  let lastY = 0;
  let smoothScroller: SmoothScroller | null = null;
  let locks = 0;

  const measure = (): ScrollState => {
    if (!win) return INITIAL;
    const y = Math.max(0, win.scrollY);
    const delta = y - lastY;
    lastY = y;
    return {
      y,
      velocity: delta,
      direction: delta > 0 ? 1 : delta < 0 ? -1 : state.direction,
      viewportHeight: win.innerHeight,
      viewportWidth: win.innerWidth,
    };
  };

  const emit = (): void => {
    state = measure();
    for (const listener of [...listeners]) listener(state);
  };

  const schedule = (): void => {
    if (!win || frame !== 0) return;
    frame = win.requestAnimationFrame(() => {
      frame = 0;
      emit();
    });
  };

  const attach = (): void => {
    if (!win || attached) return;
    attached = true;
    lastY = Math.max(0, win.scrollY);
    state = { ...measure(), velocity: 0 };
    win.addEventListener('scroll', schedule, { passive: true });
    win.addEventListener('resize', schedule, { passive: true });
  };

  const detach = (): void => {
    if (!win || !attached) return;
    attached = false;
    win.removeEventListener('scroll', schedule);
    win.removeEventListener('resize', schedule);
    if (frame !== 0) win.cancelAnimationFrame(frame);
    frame = 0;
  };

  return {
    getState: () => state,

    subscribe(listener) {
      listeners.add(listener);
      attach();
      listener(state);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) detach();
      };
    },

    refresh() {
      if (!win) return;
      if (attached) schedule();
      else state = { ...measure(), velocity: 0 };
    },

    scrollTo(y, options = {}) {
      if (!win) return;
      if (smoothScroller) smoothScroller.scrollTo(y, { immediate: options.immediate === true });
      else win.scrollTo({ top: y, behavior: options.immediate ? 'auto' : 'smooth' });
    },

    settle() {
      if (!smoothScroller || locks > 0) return;
      // stop() drops the running animation and re-bases the scroller on the window's real position;
      // start() makes it listen again. (An immediate scrollTo is not enough: it is skipped when the
      // glide's target equals the current position, which leaves the animation running.)
      smoothScroller.stop();
      smoothScroller.start();
    },

    lock() {
      locks += 1;
      smoothScroller?.stop();
      let released = false;
      return () => {
        if (released) return;
        released = true;
        locks = Math.max(0, locks - 1);
        if (locks === 0) smoothScroller?.start();
      };
    },

    registerSmoothScroller(scroller) {
      smoothScroller = scroller;
      if (scroller && locks > 0) scroller.stop();
    },

    get smooth() {
      return smoothScroller !== null;
    },
  };
}

export const scrollStore: ScrollStore = createScrollStore();
